import axiosInstance from './axiosInstance';

export interface PassthroughPageRow {
  id: string;
  pageUrl: string;
  pageName: string;
  enabled: boolean;
  interstitialEnabled: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface PassthroughGameRow {
  id: string;
  gameName: string;
  gameType: 'landscape' | 'portrait';
  passthroughEnabled: boolean;
  channelParam: string;
  passthroughUrls: string[];
  createdAt?: string | null;
  updatedAt?: string | null;
}

export class PassthroughPageApi {
  static async list(skip = 0, limit = 50): Promise<{ list: PassthroughPageRow[]; total: number }> {
    const response = await axiosInstance.get('/cms/v1/passthrough/page', {
      params: { skip, limit },
    });
    return response.data.data;
  }

  static async get(id: string): Promise<PassthroughPageRow> {
    const response = await axiosInstance.get(`/cms/v1/passthrough/page/${id}`);
    return response.data.data.page;
  }

  static async create(payload: {
    pageUrl: string;
    pageName: string;
    enabled?: boolean;
    interstitialEnabled?: boolean;
  }): Promise<PassthroughPageRow> {
    const response = await axiosInstance.post('/cms/v1/passthrough/page', payload);
    return response.data.data.page;
  }

  static async update(
    id: string,
    payload: Partial<Pick<PassthroughPageRow, 'pageUrl' | 'pageName' | 'enabled' | 'interstitialEnabled'>>
  ): Promise<PassthroughPageRow> {
    const response = await axiosInstance.put(`/cms/v1/passthrough/page/${id}`, payload);
    return response.data.data.page;
  }

  static async remove(id: string): Promise<void> {
    await axiosInstance.delete(`/cms/v1/passthrough/page/${id}`);
  }
}

export interface PassthroughH5ConfigRow {
  id: string;
  name: string;
  enabled: boolean;
  h5AdsOpen: number;
  h5AdsConfig: Record<string, unknown>;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export class PassthroughH5ConfigApi {
  static async list(): Promise<{ list: PassthroughH5ConfigRow[]; total: number }> {
    const response = await axiosInstance.get('/cms/v1/passthrough/h5config');
    return response.data.data;
  }

  static async create(payload: {
    h5AdsOpen?: number;
    h5AdsConfigJson: string;
    enabled?: boolean;
    name?: string;
  }): Promise<PassthroughH5ConfigRow> {
    const response = await axiosInstance.post('/cms/v1/passthrough/h5config', payload);
    return response.data.data.config;
  }

  static async update(
    id: string,
    payload: Partial<{
      h5AdsOpen: number;
      h5AdsConfigJson: string;
      enabled: boolean;
      name: string;
    }>
  ): Promise<PassthroughH5ConfigRow> {
    const response = await axiosInstance.put(`/cms/v1/passthrough/h5config/${id}`, payload);
    return response.data.data.config;
  }

  static async remove(id: string): Promise<void> {
    await axiosInstance.delete(`/cms/v1/passthrough/h5config/${id}`);
  }
}

export interface PassthroughProductH5ConfigRow {
  id: string;
  name: string;
  productId: string;
  versionCount: number;
  h5AdsOpen: number;
  h5AdsConfig: Record<string, unknown>;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export class PassthroughProductH5ConfigApi {
  static async list(skip = 0, limit = 50): Promise<{ list: PassthroughProductH5ConfigRow[]; total: number }> {
    const response = await axiosInstance.get('/cms/v1/passthrough/product-h5config', {
      params: { skip, limit },
    });
    return response.data.data;
  }

  static async get(id: string): Promise<PassthroughProductH5ConfigRow> {
    const response = await axiosInstance.get(`/cms/v1/passthrough/product-h5config/${id}`);
    return response.data.data.config;
  }

  static async create(payload: {
    name: string;
    productId: string;
    versionCount?: number;
    h5AdsOpen?: number;
    h5AdsConfigJson: string;
  }): Promise<PassthroughProductH5ConfigRow> {
    const response = await axiosInstance.post('/cms/v1/passthrough/product-h5config', payload);
    return response.data.data.config;
  }

  static async update(
    id: string,
    payload: Partial<{
      name: string;
      productId: string;
      versionCount: number;
      h5AdsOpen: number;
      h5AdsConfigJson: string;
    }>
  ): Promise<PassthroughProductH5ConfigRow> {
    const response = await axiosInstance.put(`/cms/v1/passthrough/product-h5config/${id}`, payload);
    return response.data.data.config;
  }

  static async remove(id: string): Promise<void> {
    await axiosInstance.delete(`/cms/v1/passthrough/product-h5config/${id}`);
  }
}

export interface PassthroughProductH5StatsRow {
  id: string;
  name: string;
  productId: string;
  versionCount: number;
  key: string;
  windowSeconds: number;
  windowStartAt?: string | null;
  remainingSeconds: number;
  isExpired: boolean;
  data: Record<string, number>;
  rawData?: Record<string, number>;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export class PassthroughProductH5StatsApi {
  static async list(
    skip = 0,
    limit = 50,
    productId?: string
  ): Promise<{ list: PassthroughProductH5StatsRow[]; total: number }> {
    const response = await axiosInstance.get('/cms/v1/passthrough/product-h5stats', {
      params: { skip, limit, productId: productId || undefined },
    });
    return response.data.data;
  }

  static async get(id: string): Promise<PassthroughProductH5StatsRow> {
    const response = await axiosInstance.get(`/cms/v1/passthrough/product-h5stats/${id}`);
    return response.data.data.stats;
  }

  static async create(payload: {
    name: string;
    productId: string;
    versionCount?: number;
    key?: string;
    initialFields?: string[];
    windowSeconds?: number;
  }): Promise<PassthroughProductH5StatsRow> {
    const response = await axiosInstance.post('/cms/v1/passthrough/product-h5stats', payload);
    return response.data.data.stats;
  }

  static async addField(id: string, fieldName: string): Promise<PassthroughProductH5StatsRow> {
    const response = await axiosInstance.put(`/cms/v1/passthrough/product-h5stats/${id}`, {
      action: 'add_field',
      fieldName,
    });
    return response.data.data.stats;
  }

  static async removeField(id: string, fieldName: string): Promise<PassthroughProductH5StatsRow> {
    const response = await axiosInstance.put(`/cms/v1/passthrough/product-h5stats/${id}`, {
      action: 'remove_field',
      fieldName,
    });
    return response.data.data.stats;
  }

  static async resetKey(id: string, newKey?: string): Promise<PassthroughProductH5StatsRow> {
    const response = await axiosInstance.put(`/cms/v1/passthrough/product-h5stats/${id}`, {
      action: 'reset_key',
      newKey,
    });
    return response.data.data.stats;
  }

  static async resetWindow(id: string): Promise<PassthroughProductH5StatsRow> {
    const response = await axiosInstance.put(`/cms/v1/passthrough/product-h5stats/${id}`, {
      action: 'reset_window',
    });
    return response.data.data.stats;
  }

  static async update(
    id: string,
    payload: Partial<{
      name: string;
      key: string;
      windowSeconds: number;
    }>
  ): Promise<PassthroughProductH5StatsRow> {
    const response = await axiosInstance.put(`/cms/v1/passthrough/product-h5stats/${id}`, payload);
    return response.data.data.stats;
  }

  static async remove(id: string): Promise<void> {
    await axiosInstance.delete(`/cms/v1/passthrough/product-h5stats/${id}`);
  }
}

export class PassthroughGameApi {
  static async list(skip = 0, limit = 50): Promise<{ list: PassthroughGameRow[]; total: number }> {
    const response = await axiosInstance.get('/cms/v1/passthrough/game', {
      params: { skip, limit },
    });
    return response.data.data;
  }

  static async get(id: string): Promise<PassthroughGameRow> {
    const response = await axiosInstance.get(`/cms/v1/passthrough/game/${id}`);
    return response.data.data.game;
  }

  static async create(payload: {
    gameName: string;
    gameType: 'landscape' | 'portrait';
    passthroughEnabled: boolean;
    channelParam: string;
    passthroughUrls?: string[];
  }): Promise<PassthroughGameRow> {
    const response = await axiosInstance.post('/cms/v1/passthrough/game', payload);
    return response.data.data.game;
  }

  static async update(
    id: string,
    payload: Partial<{
      gameName: string;
      gameType: 'landscape' | 'portrait';
      passthroughEnabled: boolean;
      channelParam: string;
      passthroughUrls: string[];
    }>
  ): Promise<PassthroughGameRow> {
    const response = await axiosInstance.put(`/cms/v1/passthrough/game/${id}`, payload);
    return response.data.data.game;
  }

  static async remove(id: string): Promise<void> {
    await axiosInstance.delete(`/cms/v1/passthrough/game/${id}`);
  }
}
